import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Button } from 'react-bootstrap5';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';

import HorizontalTextField from 'components/new_ui/Form/HorizontalTextField';
import HorizontalSelectField from 'components/new_ui/Form/HorizontalSelectField';
import ProgressBar from 'components/Widgets/ProgressBar';
import IconWithText from 'components/Widgets/IconWithText';
import SubmitButton from 'components/Widgets/SubmitButton';
import useCountries from 'components/Common/Countries/Hooks/useCountries';
import StateField from 'components/Common/Affiliates/Form/StateField';

import Actions from './Actions';

export default function AddressInformation({ affiliate, nextUrl, prevUrl }) {
  const { countries } = useCountries();

  const formSchema = Yup.object().shape({
    address: Yup.string().required('Address is required'),
    country: Yup.string().nullable().required('Country is required'),
    cityName: Yup.string().required('City is required'),
    zipCode: Yup.string().required('Zip Code is required'),
    stateName: Yup.string().nullable().required('State is required'),
  });

  const onSave = (values, { setSubmitting, setErrors }) => {
    Actions.update(
      affiliate.id,
      {
        address: values.address,
        address2: values.address2,
        country: values.country,
        state_name: values.stateName,
        city_name: values.cityName,
        zip_code: values.zipCode,
      },
      () => {
        setSubmitting(false);
        Actions.goToPage(nextUrl);
      },
      (errorData) => {
        const newErrors = {};
        errorData.errors.forEach((item) => {
          newErrors[item.source.pointer.replace('/data/attributes/', '')] = item.detail;
        });
        setErrors(newErrors);
        setSubmitting(false);
      },
    );
  };

  return (
    <div className="content">
      <section>
        <Row>
          <Col md={8}>
            <h1 className="mb-3">Tell us about yourself</h1>

            <IconWithText
              iconClassName="onboarding-address-data-icon big-icon bg-secondary"
            >
              <h5>Part 3: Address</h5>
              Please provide your business or home address for payment purposes.
            </IconWithText>
          </Col>

          <Col md={4} className="mt-3 mt-md-0">
            <ProgressBar now={75} />
          </Col>
        </Row>
      </section>

      <Formik
        validationSchema={formSchema}
        initialValues={{
          address: affiliate.attributes.address,
          address2: affiliate.attributes.address2,
          country: affiliate.attributes.country || 'US',
          stateName: affiliate.attributes.state_name,
          cityName: affiliate.attributes.city_name,
          zipCode: affiliate.attributes.zip_code,
        }}
        onSubmit={onSave}
      >
        {({ isSubmitting }) => (
          <FormikForm className="form-with-counter" noValidate="">
            <section className="border-top border-light">
              <Row>
                <Col md={{ span: 8, offset: 2 }}>
                  <HorizontalSelectField
                    className="select-counter-form"
                    name="country"
                    label="Country"
                    placeholder={countries === null ? 'Loading...' : 'Select Country'}
                    options={countries === null ? [] : countries}
                    required
                  />

                  <HorizontalTextField name="address" label="Address 1" required />

                  <HorizontalTextField name="address2" label="Address 2" />

                  <HorizontalTextField name="cityName" label="City" required />

                  <StateField className="select-counter-form" />

                  <HorizontalTextField name="zipCode" label="Zip Code" required />
                </Col>
              </Row>
            </section>

            <footer className="d-flex justify-content-between">
              <Button
                variant="outline-primary"
                disabled={isSubmitting}
                onClick={() => { Actions.goToPage(prevUrl); }}
              >
                Previous
              </Button>

              <SubmitButton loading={isSubmitting}>
                Next
              </SubmitButton>
            </footer>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
}

AddressInformation.propTypes = {
  affiliate: PropTypes.object.isRequired,
  nextUrl: PropTypes.string.isRequired,
  prevUrl: PropTypes.string.isRequired,
};
